import React, { useContext } from 'react';
import { connect } from 'react-redux';
import * as yup from 'yup';
import PropTypes from 'prop-types';
import config from '../../../../../../../../../config/config.json';
import {
  manualOrdersMapperSchema,
  manualOrdersTypes,
  typeMapperKeys,
  vpoolTypesMapper,
} from '../../../../../../../../../constants/vpool';
import { usePromise } from '../../../../../../../../../hooks/usePromise/usePromise';
import { assetAmountInWei } from '../../../../../../../../../services/assetsService';
import { getBaseToken } from '../../../../../../../../../services/createHPoolService';
import { getSignatureConfig, updateTradeOrder } from '../../../../../../../../../services/hpoolService';
import { sendManualSwaps } from '../../../../../../../../../services/vpoolService';
import { signTypedDataV4 } from '../../../../../../../../../services/web3Service';
import { combineLoadingMsgs, getTokenSymbol } from '../../../../../../../../../utils/format';
import {
  getValueByMultiProps,
  multiConver,
  objectMapper,
  removeObjKeys,
} from '../../../../../../../../../utils/mapper';
import { createValidation } from '../../../../../../../../../utils/validation';
import { Button, Fields } from '../../../../../../../../Common';
import WalletValidator from '../../../../../../../../hoc/WalletValidator/WalletValidator';
import { OrdersContext } from '../../../../../../../../Modals/EditTokensModal/EditOrderDetails';
import { ManualOrderContext } from '../../../../../../../../Modals/ManualOrderModals/ManualOrderModals';
import { getTokenDecimals } from '../../../../../../../../../services/utilsService';

const fields = [
  {
    name: 'amountUSDToWithdraw',
    id: 'amount_to_withdraw',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.amount_of_usd_to_withdraw',
    tag: 'number',
    placeholder: 0,
  },
  {
    name: 'amountOfTokensToReturn',
    id: 'amount_to_return',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.amount_of_tokens_to_return',
    tag: 'number',
    placeholder: 0,
  },
  {
    name: 'stableCoinSymbol',
    id: 'coin_symbol',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.token_to_withdraw_symbol',
    placeholder: 'USDT',
  },
  {
    name: 'tokenToReceiveSymbol',
    id: 'token_to_receive',
    className: 'w-50 fill-at-mobile',
    label: 'vpool.token_to_receive_symbol',
    placeholder: 'BNB',
  },
];

const validationSchema = createValidation({
  amountUSDToWithdraw: yup.mixed().required(''),
  amountOfTokensToReturn: yup.mixed().required(''),
});

function OpenOrder({ hpool }) {
  const { network: chainId, stableCoins } = config;

  const { selectedToken } = useContext(OrdersContext);
  // const { id: tokenId } = selectedToken || {}; // TODO integrate this when u have real values
  const { orderInfo, passedValues, onSubmit = () => {} } = useContext(ManualOrderContext);
  const { token_id: passedTokenId, orderId: passedOrderId } = passedValues ?? {};

  const { id: poolId, assets = [] } = hpool;

  const tokenToReceiveSymbol = 'lst' || getTokenSymbol(selectedToken);
  const stableCoin = getBaseToken(assets, stableCoins);
  const stableCoinSymbol = 'usdt' || getTokenSymbol(stableCoin);

  const [sign, , signing] = usePromise(signTypedDataV4, { showError: true });
  const [sendOrder, , sendingOrder] = usePromise(sendManualSwaps, { showError: true });
  const [updateOrder, , updatingOrder] = usePromise(updateTradeOrder, { showError: true });
  const loading = false;

  const currentLoading = combineLoadingMsgs([
    { isLoading: signing, loadingText: 'Signing' },
    { isLoading: sendingOrder, loadingText: 'Creating Order' },
    { isLoading: updatingOrder, loadingText: 'Updating Order' },
  ]);

  const createOrder = (values) => {
    const orderType = manualOrdersTypes.MANUAL_OPEN_ORDER.toLowerCase();
    const body = {
      ...values,
      type: orderType,
      vpoolId: +poolId,
      chainId,
    };
    const mappedForRequest = objectMapper(body, manualOrdersMapperSchema);
    return sendOrder(mappedForRequest);
  };

  const handleSubmit = async (values) => {
    const { type, primaryType } = getSignatureConfig('OpenOrder', vpoolTypesMapper);
    const tokenDecimals = await getTokenDecimals(values.tokenAddress);
    const { trade_order: tradeOrder = orderInfo } = orderInfo ? {} : (await createOrder(values)) || {};
    const { id: tradeOrderId = passedOrderId, token_id: tokenId = passedTokenId } = tradeOrder;
    let msg = { poolId, tokenId, ...values };

    msg = multiConver(
      [
        {
          keys: typeMapperKeys('OpenOrder'),
          convert: (value) => +value,
        },
        {
          keys: ['amountUSDToWithdraw', 'amountOfTokensToReturn'],
          convert: (value) => assetAmountInWei(+value, tokenDecimals),
        },
      ],
      removeObjKeys(msg, typeMapperKeys('OpenOrder')),
    );
    const signature = await sign(msg, type, primaryType, true);
    updateOrder(tradeOrderId, { signature, delete: false }).then(onSubmit);
  };

  const { onPrev } = useContext(OrdersContext);

  const handleClick = () => {
    onPrev();
    onSubmit();
  };

  return (
    <Fields fields={fields} validationSchema={validationSchema} onSubmit={handleSubmit} initialValues={passedValues}>
      {({ disabled, errors, defaultWrapperClass, values }) => (
        <div className={defaultWrapperClass}>
          <Button className="button back" onClick={handleClick} text="common.cancel" />
          <WalletValidator>
            <Button
              type="submit"
              className="button green"
              text="common.create"
              loadingText={currentLoading.loadingText}
              loading={currentLoading.isLoading}
              disabled={disabled || currentLoading.isLoading}
              requireWallet
            />
          </WalletValidator>
        </div>
      )}
    </Fields>
  );
}

OpenOrder.propTypes = {
  hpool: PropTypes.object.isRequired,
};

const mapStateToProps = ({ createHPool }) => ({
  hpool: createHPool.hpool,
});

export default connect(mapStateToProps)(OpenOrder);
